import { useCallback, useEffect, useState } from "react";
import { Body1, Button, Spinner, Subtitle2, makeStyles, tokens } from "@fluentui/react-components";
import { Add24Regular, ArrowClockwise24Regular, Cart24Regular } from "@fluentui/react-icons";
import { useData } from "../../data/DataProvider";
import { loadMine, type Richiesta } from "../../data/richieste";
import { navigate } from "../../lib/nav";
import { Shell } from "../../components/Shell";
import { RichiestaCard } from "../../components/RichiestaCard";
import { ErrorState } from "../../components/ErrorState";
import type { ReactNode } from "react";

const useStyles = makeStyles({
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalL}`,
    color: tokens.colorNeutralForeground2,
  },
  icon: { fontSize: "40px", color: tokens.colorBrandForeground1 },
});

export function MieRichieste({ switcher }: { switcher?: ReactNode }) {
  const s = useStyles();
  const data = useData();
  const [items, setItems] = useState<Richiesta[]>();
  const [nextLink, setNextLink] = useState<string>();
  const [error, setError] = useState<unknown>();

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const page = await loadMine(data);
      setItems(page.items);
      setNextLink(page.nextLink);
    } catch (e) {
      setError(e);
    }
  }, [data]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    const page = await loadMine(data, nextLink);
    setItems((p) => [...(p ?? []), ...page.items]);
    setNextLink(page.nextLink);
  };

  return (
    <Shell
      title="Le tue richieste"
      actions={
        <>
          {switcher}
          <Button appearance="subtle" icon={<ArrowClockwise24Regular />} aria-label="Aggiorna" onClick={() => { setItems(undefined); void load(); }} />
        </>
      }
      footer={
        <Button appearance="primary" size="large" icon={<Add24Regular />} style={{ flexGrow: 1 }} onClick={() => navigate("/nuova")}>
          Nuova richiesta
        </Button>
      }
    >
      {error ? (
        <ErrorState error={error} />
      ) : !items ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : items.length === 0 ? (
        <div className={s.empty}>
          <Cart24Regular className={s.icon} />
          <Subtitle2>Chiedi un acquisto</Subtitle2>
          <Body1>Cancelleria, mensa, materiale per stampanti o altro: le richieste che invii compaiono qui, con lo stato aggiornato dai gestori.</Body1>
        </div>
      ) : (
        <>
          {items.map((r) => (
            <RichiestaCard key={r.id} r={r} onOpen={() => navigate(`/r/${r.id}`)} />
          ))}
          {nextLink && <Button onClick={more}>Mostra altre</Button>}
        </>
      )}
    </Shell>
  );
}
